import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, MapPin } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import Reveal from "./Reveal";

const empty = { name: "", email: "", company: "", phone: "", message: "" };

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      toast.success(t("f_success"));
      setForm(empty);
    } catch (err) {
      toast.error(t("f_error"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-black/12 py-4 font-body text-[#0f1115] placeholder:text-[#9aa1ac] focus:border-[#d81f2a] focus:outline-none transition-colors";

  return (
    <section id="contact" data-testid="contact" className="relative bg-[#ffffff] py-24 md:py-32">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div>
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#d81f2a]">
              {t("contact_overline")}
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-bold uppercase leading-[0.95] tracking-tight text-[#0f1115]">
              {t("contact_title")}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-md font-body text-base text-[#5b6472] leading-relaxed">
              {t("contact_sub")}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12 space-y-4 font-mono text-sm text-[#5b6472]">
              <div className="text-[#0f1115]">DMD GRUP DIŞ TİCARET LTD ŞTİ</div>
              <a href="tel:+905364869008" data-testid="contact-phone-link" className="block hover:text-[#d81f2a] transition-colors">
                +90 536 486 90 08
              </a>
              <a href="mailto:info@dmdgroup.com.tr" data-testid="contact-email-link" className="block hover:text-[#d81f2a] transition-colors">
                info@dmdgroup.com.tr
              </a>
              <a href="https://dmdgroup.com.tr" target="_blank" rel="noreferrer" className="block hover:text-[#d81f2a] transition-colors">
                dmdgroup.com.tr
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-10" data-testid="offices">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#d81f2a]">{t("offices_title")}</div>
              <div className="mt-4 grid grid-cols-2 gap-px bg-black/10 border border-black/10">
                {[
                  { city: "TASHKENT", country: "Özbekistan" },
                  { city: "ASHGABAT", country: "Türkmenistan" },
                  { city: "DUBAI", country: "BAE / UAE" },
                  { city: "KHORGOS", country: "Çin Sınırı / China" },
                ].map((o) => (
                  <div key={o.city} className="bg-white p-5 flex items-start gap-3" data-testid={`office-${o.city.toLowerCase()}`}>
                    <MapPin className="w-4 h-4 text-[#d81f2a] shrink-0 mt-1" />
                    <div>
                      <div className="font-display text-base font-bold uppercase tracking-tight text-[#0f1115]">{o.city}</div>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-[#5b6472]">{o.country}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input data-testid="contact-name" required name="name" value={form.name} onChange={onChange} placeholder={t("f_name")} className={inputCls} />
              <input data-testid="contact-email" required type="email" name="email" value={form.email} onChange={onChange} placeholder={t("f_email")} className={inputCls} />
              <input data-testid="contact-company" name="company" value={form.company} onChange={onChange} placeholder={t("f_company")} className={inputCls} />
              <input data-testid="contact-phone" name="phone" value={form.phone} onChange={onChange} placeholder={t("f_phone")} className={inputCls} />
            </div>
            <textarea data-testid="contact-message" required name="message" value={form.message} onChange={onChange} placeholder={t("f_message")} rows={4} className={inputCls + " resize-none"} />
            <button
              data-testid="contact-submit"
              type="submit"
              disabled={loading}
              className="group inline-flex items-center gap-3 bg-[#d81f2a] text-[#ffffff] px-8 py-4 font-mono text-xs uppercase tracking-[0.15em] font-bold hover:bg-white transition-colors disabled:opacity-50"
            >
              {loading ? t("f_sending") : t("f_send")}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
